/* eslint-disable max-lines */
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Translate } from 'app/components/translate';
import { GeneralConstants } from 'app/constants/general-constants';
import { SeamStatus } from 'app/constants/seam-status';
import { CategoryImpact } from 'app/model/redshift/impact-report/category-impact';
import { ChannelImpact } from 'app/model/redshift/impact-report/channel-impact';
import { StoreImpact } from 'app/model/redshift/impact-report/store-impact';
import { createEmptySave, SavedReports } from 'app/model/redshift/saved-reports';
import { messagingService, seamImpactReportService } from 'app/services';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

import { selectSelectedRequest } from '../../slice';

export const ImpactReportList: React.FunctionComponent = () => {
    const request = useSelector(selectSelectedRequest);

    const [display, setDisplay] = useState('current');
    const [type, setType] = useState('store');
    const [saved, setSaved] = useState<SavedReports>(createEmptySave());
    const [currentStores, setCurrentStores] = useState<StoreImpact[]>([]);
    const [currentChannels, setCurrentChannels] = useState<ChannelImpact[]>([]);
    const [currentCategories] = useState<CategoryImpact[]>([]);
    const [isProd, setIsProd] = useState(false);

    const disableSave =
        request && [SeamStatus.CLOSED, SeamStatus.PROD, SeamStatus.CANCELLED].includes(request.currentPlace);
    const saveTooltipKey = disableSave ? 'seam.request.evidence.no_change' : 'seam.request.evidence.replace';
    const disableSaved = 0 === saved.store.length + saved.channel.length + saved.category.length;

    useEffect(() => {
        if (!request) {
            // this should never be the case, but if we don't have request we can't load anything
            return undefined;
        }

        const inflightGet = seamImpactReportService.getSavedEvidence(request, isProd).subscribe((data) => {
            if (data) {
                // this could be null
                setSaved(data);
            }
        });

        const inflightStoreList = seamImpactReportService
            .listAll({
                pageSize: GeneralConstants.PAGE_SIZE_HUGE,
                extraParams: { type: 'store', prod: isProd, storeId: request.store.id },
            })
            .subscribe((data) => setCurrentStores(data as StoreImpact[]));

        const inflightChannelList = seamImpactReportService
            .listAll({
                pageSize: GeneralConstants.PAGE_SIZE_HUGE,
                extraParams: { type: 'channel', prod: isProd },
            })
            .subscribe((data) => setCurrentChannels(data as ChannelImpact[]));
        //
        // const inflightCategoryList = seamImpactReportService
        //     .listAll({
        //         pageSize: GeneralConstants.PAGE_SIZE_HUGE,
        //         extraParams: { type: 'category', prod: isProd, categoryId: request.store.brand?.category?.id },
        //     })
        //     .subscribe((data) => setCurrentCategories(data as CategoryImpact[]));

        return () => {
            inflightGet.unsubscribe();
            inflightStoreList.unsubscribe();
            inflightChannelList.unsubscribe();
            // inflightCategoryList.unsubscribe();
        };
    }, [request, isProd]);

    if (!request) {
        return <></>;
    }

    const columns: Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>> = [];
    let rows: Array<CategoryImpact | ChannelImpact | StoreImpact> = [];

    switch (type) {
        case 'category':
            columns.push(...(categoryColumns as Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>>));
            rows = 'current' === display ? currentCategories : saved.category;
            break;
        case 'channel':
            columns.push(...(channelColumns as Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>>));
            rows = 'current' === display ? currentChannels : saved.channel;
            break;
        case 'store':
            columns.push(...(storeColumns as Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>>));
            rows = 'current' === display ? currentStores : saved.store;
            break;
        default:
            throw new Error('Unexpected type');
    }
    columns.push(...commonColumns);

    return (
        <>
            <Typography>
                <strong>Cur</strong> - Current Data | <strong>Ref</strong> - Reference Data. In pre-prod mode{' '}
                <strong>Cur</strong> is the preprod data and <strong>Ref</strong> is the Prod data. In Prod mode,{' '}
                <strong>Cur</strong> is the latest Prod data and <strong>Ref</strong> is the Sunday data.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                    <Typography display='inline'>Prod Mode: </Typography>
                    <FormControlLabel
                        control={
                            <Switch checked={isProd} onChange={(evt, checked) => setIsProd(checked)} name='prod' />
                        }
                        label={isProd ? 'On' : 'Off'}
                    />
                    <FormControl>
                        <RadioGroup
                            value={display}
                            row
                            name='radio-buttons-group'
                            onChange={(evt, val) => setDisplay(val)}
                        >
                            <FormControlLabel value='current' control={<Radio />} label='Current Data' />
                            <FormControlLabel
                                value='saved'
                                control={<Radio />}
                                label='Saved Data'
                                disabled={disableSaved}
                            />
                        </RadioGroup>
                    </FormControl>
                    {'current' === display ? (
                        <Tooltip title={<Translate tkey={saveTooltipKey} />} placement='top'>
                            <span>
                                <Button
                                    variant='contained'
                                    disabled={disableSave}
                                    onClick={() => {
                                        seamImpactReportService
                                            .saveEvidence(
                                                {
                                                    category: currentCategories,
                                                    channel: currentChannels,
                                                    store: currentStores,
                                                },
                                                request,
                                                isProd,
                                            )
                                            .subscribe((data) => {
                                                messagingService.showSuccess('Evidence Saved');
                                                setSaved(data.data);
                                            });
                                    }}
                                >
                                    Save Evidence
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        ''
                    )}
                </Box>
                <Box>
                    <FormControl>
                        <RadioGroup value={type} row name='radio-buttons-group' onChange={(evt, val) => setType(val)}>
                            <FormControlLabel value='store' control={<Radio />} label='Store' />
                            <FormControlLabel value='channel' control={<Radio />} label='Channel' />
                            <FormControlLabel value='category' disabled control={<Radio />} label='Category' />
                        </RadioGroup>
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                <DataGridPro
                    columns={columns}
                    initialState={{ columns: { columnVisibilityModel: { id: false } } }}
                    density='compact'
                    rows={rows}
                />
            </Box>
        </>
    );
};

const categoryColumns: Array<GridColDef<CategoryImpact>> = [
    {
        field: 'preprod.catId',
        headerName: 'Cur Cat Id',
        valueGetter: (params) => params.row.preprod.catId,
    },
    {
        field: 'preprod.category',
        headerName: 'Cur Category',
        valueGetter: (params) => params.row.preprod.category,
    },

    {
        field: 'prod.catId',
        headerName: 'Ref Cat Id',
        valueGetter: (params) => params.row.prod.catId,
    },
    {
        field: 'prod.category',
        headerName: 'Ref Category',
        valueGetter: (params) => params.row.prod.category,
    },
];

const channelColumns: Array<GridColDef<ChannelImpact>> = [
    {
        field: 'preprod.channel',
        headerName: 'Cur Channel',
        valueGetter: (params) => params.row.preprod.channel,
    },
    {
        field: 'prod.channel',
        headerName: 'Ref Channel',
        valueGetter: (params) => params.row.prod.channel,
    },
    {
        field: 'preprod.storeType',
        headerName: 'Cur Type',
        valueGetter: (params) => params.row.preprod.storeType,
    },
    {
        field: 'prod.storeType',
        headerName: 'Ref Type',
        valueGetter: (params) => params.row.prod.storeType,
    },
];

const storeColumns: Array<GridColDef<StoreImpact>> = [
    {
        field: 'preprod.storeId',
        headerName: 'Cur Store Id',
        valueGetter: (params) => params.row.preprod.storeId,
    },
    {
        field: 'preprod.storeName',
        headerName: 'Cur Store Name',
        valueGetter: (params) => params.row.preprod.storeName,
    },
    {
        field: 'preprod.storeType',
        headerName: 'Cur Type',
        valueGetter: (params) => params.row.preprod.storeType,
    },
    {
        field: 'preprod.channel',
        headerName: 'Cur Channel',
        valueGetter: (params) => params.row.preprod.channel,
    },
    {
        field: 'preprod.category',
        headerName: 'Cur Cat Id',
        valueGetter: (params) => params.row.preprod.category,
    },
    {
        field: 'preprod.categoryName',
        headerName: 'Cur Cat',
        valueGetter: (params) => params.row.preprod.categoryName,
    },
    {
        field: 'prod.storeId',
        headerName: 'Ref Store Id',
        valueGetter: (params) => params.row.prod.storeId,
    },
    {
        field: 'prod.storeName',
        headerName: 'Ref Store Name',
        valueGetter: (params) => params.row.prod.storeName,
    },
    {
        field: 'prod.storeType',
        headerName: 'Ref Type',
        valueGetter: (params) => params.row.prod.storeType,
    },
    {
        field: 'prod.channel',
        headerName: 'Ref Channel',
        valueGetter: (params) => params.row.prod.channel,
    },
    {
        field: 'prod.category',
        headerName: 'Ref Cat Id',
        valueGetter: (params) => params.row.prod.category,
    },
    {
        field: 'prod.categoryName',
        headerName: 'Ref Cat',
        valueGetter: (params) => params.row.prod.categoryName,
    },
];

const commonColumns: Array<GridColDef<CategoryImpact | ChannelImpact | StoreImpact>> = [
    { field: 'id', headerName: 'ID', type: 'number' },
    {
        field: 'trans',
        headerName: 'Transactions',
        type: 'number',
        valueFormatter: DataGridFormatters.numberLocaleFormatter,
    },
    {
        field: 'mids',
        headerName: 'MIDs',
        type: 'number',
        valueFormatter: DataGridFormatters.numberLocaleFormatter,
    },
    {
        field: 'spend',
        headerName: 'Cur Spend',
        type: 'number',
        valueFormatter: DataGridFormatters.currencyLocaleFormatter,
    },
    {
        field: 'prodSpendTotal',
        headerName: 'Ref Spend',
        type: 'number',
        valueFormatter: DataGridFormatters.currencyLocaleFormatter,
    },
    {
        field: 'pctProdAffected',
        headerName: '% Affected',
        type: 'number',
        valueFormatter: DataGridFormatters.numberLocaleFormatterFn({
            maximumFractionDigits: 4,
            minimumFractionDigits: 4,
        }),
    },
    {
        field: 'runDate',
        headerName: 'Run Date',
        valueFormatter: (params) => (params.value ? parseISO(params.value as string).toLocaleDateString() : ''),
    },
];
